import React, { useState, useEffect } from "react";
import { View, Pressable, Modal, TouchableWithoutFeedback } from "react-native";
import StyledText from "./common/ui/styled-text";
import { DateRange, DefinedRange } from "react-date-range";
import { addDays } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Calendar, ChevronDown, ShoppingBag, X } from "lucide-react-native";

export interface CallsFilterProps {
  onDateRangeChange: (dateRange: { from: Date; to: Date } | undefined) => void;
  onOrdersOnlyChange?: (showOrdersOnly: boolean) => void;
  dateRange?: { from: Date; to: Date } | undefined;
  showOrdersOnly?: boolean;
  isStreamOrdering?: boolean;
}

type DateRangeType =
  | {
      startDate: Date;
      endDate: Date;
      key: string;
    }[]
  | undefined;

export default function CallsFilter({
  onDateRangeChange,
  onOrdersOnlyChange,
  dateRange: externalDateRange,
  showOrdersOnly: externalShowOrdersOnly = false,
  isStreamOrdering = false,
}: CallsFilterProps) {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [localDateRange, setLocalDateRange] = useState<DateRangeType>([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: "selection",
    },
  ]);

  useEffect(() => {
    if (externalDateRange) {
      setLocalDateRange([
        {
          startDate: externalDateRange.from,
          endDate: externalDateRange.to,
          key: "selection",
        },
      ]);
    } else {
      setLocalDateRange([
        {
          startDate: new Date(),
          endDate: addDays(new Date(), 7),
          key: "selection",
        },
      ]);
    }
  }, [externalDateRange]);

  const handleDateRangeChange = (ranges: any) => {
    if (!ranges?.selection) return;
    setLocalDateRange([ranges.selection]);
  };

  const handleApplyDateRange = () => {
    if (localDateRange?.[0]?.startDate && localDateRange?.[0]?.endDate) {
      onDateRangeChange({
        from: localDateRange[0].startDate,
        to: localDateRange[0].endDate,
      });
    }
    setIsDatePickerOpen(false);
  };

  const handleCancelDateRange = () => {
    if (externalDateRange) {
      setLocalDateRange([
        {
          startDate: externalDateRange.from,
          endDate: externalDateRange.to,
          key: "selection",
        },
      ]);
    } else {
      setLocalDateRange([
        {
          startDate: new Date(),
          endDate: addDays(new Date(), 7),
          key: "selection",
        },
      ]);
    }
    setIsDatePickerOpen(false);
  };

  const handleOrdersOnlyToggle = (value: boolean) => {
    onOrdersOnlyChange?.(value);
  };

  const clearDateFilter = () => {
    onDateRangeChange(undefined);
    setIsDatePickerOpen(false);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const formatDateRange = () => {
    if (!externalDateRange?.from || !externalDateRange?.to) return "All time";
    if (
      externalDateRange.from.toDateString() ===
      externalDateRange.to.toDateString()
    ) {
      return formatDate(externalDateRange.from);
    }
    return `${formatDate(externalDateRange.from)} - ${formatDate(
      externalDateRange.to
    )}`;
  };

  return (
    <View className="w-full">
      <View className="flex-row items-center gap-2">
        {isStreamOrdering && (
          <>
            {externalShowOrdersOnly ? (
              <View className="flex-row items-center gap-2 bg-gray-800 px-3 py-1.5 rounded-md">
                <ShoppingBag size={12} color="#FFFFFF" />
                <StyledText className="text-sm text-white">
                  Orders Only
                </StyledText>
                <Pressable
                  onPress={() => handleOrdersOnlyToggle(false)}
                  className="hover:bg-gray-700 rounded-full p-0.5"
                >
                  <X size={12} color="#FFFFFF" />
                </Pressable>
              </View>
            ) : (
              <Pressable
                onPress={() => handleOrdersOnlyToggle(true)}
                className="h-8 px-3 py-1.5 gap-2 border border-border rounded-md flex-row items-center bg-white"
              >
                <ShoppingBag size={12} color="#6B7280" />
                <StyledText className="text-sm text-gray-500">
                  Orders Only
                </StyledText>
              </Pressable>
            )}
          </>
        )}

        {externalDateRange?.from && externalDateRange?.to ? (
          <Pressable
            onPress={() => setIsDatePickerOpen(true)}
            className="flex-row items-center gap-1 bg-gray-800 text-white px-3 py-1.5 rounded-md"
          >
            <Calendar size={12} color="#FFFFFF" />
            <StyledText className="text-sm text-white">
              {formatDateRange()}
            </StyledText>
            <Pressable
              onPress={clearDateFilter}
              className="ml-1 hover:bg-gray-700 rounded-full p-0.5"
            >
              <X size={12} color="#FFFFFF" />
            </Pressable>
          </Pressable>
        ) : (
          <Pressable
            onPress={() => setIsDatePickerOpen(true)}
            className="h-8 px-3 py-1.5 border border-border rounded-md flex-row items-center bg-white"
          >
            <Calendar size={12} color="#6B7280" className="mr-2" />
            <StyledText className="text-sm text-gray-500">
              Date Range
            </StyledText>
            <ChevronDown size={12} color="#6B7280" className="ml-2" />
          </Pressable>
        )}
      </View>

      <Modal
        visible={isDatePickerOpen}
        transparent={true}
        animationType="fade"
        onRequestClose={handleCancelDateRange}
      >
        <TouchableWithoutFeedback onPress={handleCancelDateRange}>
          <View className="flex-1 bg-black/50 justify-center items-center">
            <TouchableWithoutFeedback>
              <View className="bg-white rounded-xl p-5 w-[95%] max-w-[600px]">
                <View className="flex-col sm:flex-row gap-4">
                  <View className="hidden sm:flex sm:flex-[1]">
                    <DefinedRange
                      onChange={handleDateRangeChange}
                      ranges={localDateRange}
                      inputRanges={[]}
                      className="!w-full"
                    />
                  </View>
                  <View className="flex-1 sm:flex-[2]">
                    <DateRange
                      onChange={handleDateRangeChange}
                      moveRangeOnFirstSelection={false}
                      ranges={localDateRange}
                      rangeColors={["#1F2937"]}
                      className="w-full"
                    />
                  </View>
                </View>
                <View className="flex-row justify-end mt-4 gap-2">
                  <Pressable
                    onPress={handleCancelDateRange}
                    className="px-4 py-2 rounded-md border border-border"
                  >
                    <StyledText className="text-sm text-gray-600">
                      Cancel
                    </StyledText>
                  </Pressable>
                  <Pressable
                    onPress={handleApplyDateRange}
                    className="px-4 py-2 rounded-md bg-gray-800"
                  >
                    <StyledText className="text-sm text-white">
                      Apply
                    </StyledText>
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
}
